import React from 'react';

import classnames from 'classnames';

import { Icon } from 'styled-system';

import useTranslation from '../../../features/locale/useTranslation';

import styles from './ProgressLoading.module.scss';

type Item = {
    id: string;
    name: string;
    bytesTotal?: number;
    bytesUploaded?: number;
    isUploaded?: boolean;
    error?: string;
    isDefaultError?: boolean;
    onRetry?: () => void;
};

type ProgressLoadingProps = {
    items: Item[];
    onClearItem: (id: string) => void;
};

export const ProgressLoading = ({ items, onClearItem }: ProgressLoadingProps) => {
    const { t } = useTranslation();
    const renderFileProgress = (progress: number, complete: boolean) => {
        if (complete) {
            return <Icon name="s-icon-circle-check" />;
        }
        if (progress < 100) {
            return `${progress}%`;
        }
        return t('Processing...');
    };
    return items.map((item, idx) => {
        const { bytesUploaded, bytesTotal } = item;
        const hasSize = bytesUploaded && bytesTotal;
        const percentage = hasSize ? parseInt(String((bytesUploaded * 100) / bytesTotal), 10) : 0;
        return (
            <div
                key={item.id}
                className={classnames(styles.progressLoading, {
                    [styles['progressLoading--success']]: item.isUploaded,
                    [styles['progressLoading--error']]: item.error
                })}
            >
                <div className={styles.fileInfo}>
                    <div>
                        <div>{item.name}</div>
                        {item.error && <div className={styles.errorMessage}>{item.error}</div>}
                    </div>
                    {item.error ? (
                        <div className={styles.fileProgress}>
                            <Icon
                                onClick={() => onClearItem(item.id)}
                                name="s-icon-circle-xmark"
                                tabIndex={idx}
                            />
                        </div>
                    ) : (
                        <div className={styles.fileProgress}>
                            {renderFileProgress(percentage, !!item.isUploaded)}
                        </div>
                    )}
                </div>
                {!item.error && !item.isUploaded && (
                    <div className={styles.progressBar} style={{ width: `${percentage}%` }} />
                )}
            </div>
        );
    });
};

ProgressLoading.displayName = 'ProgressLoading';
